// @flow
import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";
import * as sharedSelectors from "@modules/shared/selectors";
import * as serviceSelectors from "@modules/service/selectors";
import type { HierarchyTree } from "@modules/shared/reducer/hierarchy/types";
import actionCreators from "@modules/service/actionCreator";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  CollapsibleWizard,
  LinearWizard,
  Step,
} from "@components";
import type { HierarchyAssignment } from "@modules/service/types";
import type { Referral } from "@modules/service/reducer/referrals/types";

import StepSupplierDetails from "./step-supplier-details";
import StepManagerReferrals from "./step-manager-referrals";
import StepEmployeeReferrals from "./step-employee-referrals";
import StepSupplierTracking from "./step-supplier-tracking";
import StepManualReferrals from "./step-manual-referrals";
import StepHierarchyScope from "./step-hierarchy-scope";
import { removeNullAndUndefinedProps } from "@transformers/object";
import { emailPattern } from "@utils/schema";

type OwnProps = $Exact<{
  showModal: boolean,
  defaultStep: 1 | 2 | 3 | 4 | 5,
  clientId: number,
  editing: boolean,
  container: ?Referral,
  currentFilter: boolean,
  defaultStep: number,
  onClose: () => void,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  draft: ?Referral,
  assignments: Array<HierarchyAssignment>,
  hierarchy: ?HierarchyTree,
}>;

type PropsFromDispatch = $Exact<{
  setDraft: typeof actionCreators.SetReferralDraft,
  setAssignments: typeof actionCreators.SetReferralAssignments,
  add: typeof actionCreators.AddReferral,
  update: typeof actionCreators.UpdateReferral,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

export class ReferralAddEditModal extends React.Component<Props> {
  validator: *;
  errorMessage: string | boolean;

  get draftData() {
    return this.props.draft || {};
  }

  validateSupplierDetails = () => {
    const supplierDetailsSchema = (this.schema || {}).supplierDetails;
    const data = removeNullAndUndefinedProps(this.draftData);
    this.validator = new Ajv().compile(supplierDetailsSchema);
    return this.validator(data) && emailPattern.test(data.email);
  };

  validateManagerReferralsSettings = () => {
    if (this.draftData.managerReferrals === false) return true;
    const data = removeNullAndUndefinedProps(this.draftData.managerReferralsSettings);
    this.validator = new Ajv().compile((this.schema || {}).managerLedReferrals);
    const isValid =
      this.validator(data) &&
      data.declinedReasons.filter(reason => reason.isActive === true).length > 0; // At least one active reason!

    return isValid;
  };

  validateEmployeeReferralsSettings = () => {
    if (this.draftData.employeeReferrals === false) return true;
    const data = removeNullAndUndefinedProps(this.draftData.employeeReferralsSettings);
    this.validator = new Ajv().compile((this.schema || {}).employeeLedReferrals);
    const isValid =
      this.validator(data) &&
      data.declinedReasons.filter(reason => reason.isActive === true).length > 0; // At least one active reason!
    return isValid;
  };

  validateAlertsConfiguration = () => {
    const managerReferralsSettings = removeNullAndUndefinedProps(
      this.draftData.managerReferralsSettings,
    );
    const employeeReferralsSettings = removeNullAndUndefinedProps(
      this.draftData.employeeReferralsSettings,
    );

    const managerAlerts = (managerReferralsSettings.alertConfigurations || []).map(a => a.id);
    const employeeAlerts = (employeeReferralsSettings.alertConfigurations || []).map(a => a.id);
    const similar = employeeAlerts.filter(emplId => managerAlerts.indexOf(emplId) !== -1);
    return similar.length < 1;
  };

  validateSupplierTracking = () => {
    this.validator = new Ajv().compile((this.schema || {}).supplierTracking);

    let res = this.validator(removeNullAndUndefinedProps(this.draftData.supplierTracking));
    if (
      (this.draftData.supplierTracking || {}).declinedReasons &&
      this.draftData.supplierTracking.requiredResponse === "ACCEPT_REFUSE"
    ) {
      res =
        (this.draftData.supplierTracking || {}).declinedReasons.filter(
          reason => reason.isActive === true,
        ).length > 0; // At least one active reason!;
    }
    if (((this.draftData.supplierTracking || {}).outcomes || []).length < 1) {
      res = false;
    }
    return res;
  };

  validateManualReferralsSettings = () => {
    if (this.draftData.manualReferrals === false) return true;
    const data = removeNullAndUndefinedProps(this.draftData.manualReferralsSettings);

    this.validator = new Ajv().compile(this.schema.manualReferralsSettings);

    let res = this.validator(data);
    if ((data.reasons || []).length < 1) {
      res = false;
    }
    return res;
  };

  get nextButtonLabel() {
    const { managerReferrals, employeeReferrals, manualReferrals } = this.stepData[0].data;

    const t = this.props.t;
    let supplierDetailsLabel = this.props.t("NEXT_SUPPLIER_TRACKING");
    let managerReferralsLabel = this.props.t("NEXT_SUPPLIER_TRACKING");
    let manualReferralsLabel = this.props.t("NEXT_HIERARCHY_SCOPE");
    let supplierTrackingLabel = this.props.t("NEXT_HIERARCHY_SCOPE");

    if (manualReferrals === true) {
      supplierTrackingLabel = t("NEXT_MANUAL_REFERRALS");
    }
    if (employeeReferrals === true) {
      supplierDetailsLabel = t("NEXT_EMPLOYEE_LED_REFERRALS");
      managerReferralsLabel = t("NEXT_EMPLOYEE_LED_REFERRALS");
    }
    if (managerReferrals === true) {
      supplierDetailsLabel = t("NEXT_MANAGER_LED_REFERRALS");
    }

    return {
      supplierDetailsLabel,
      managerReferralsLabel,
      supplierTrackingLabel,
      manualReferralsLabel,
    };
  }

  get schema() {
    return this.props.schema || {};
  }

  get areStepsValid() {
    return (
      this.validateSupplierDetails() &&
      this.validateManagerReferralsSettings() &&
      this.validateEmployeeReferralsSettings() &&
      this.validateAlertsConfiguration() &&
      this.validateSupplierTracking() &&
      this.validateManualReferralsSettings()
    );
  }

  handleCancel = () => {
    this.props.setDraft(null);
    this.props.onClose();
  };

  handleSubmit = () => {
    const data = {
      clientId: this.props.clientId,
      processId: ((this.props || {}).container || {}).id,
      draft: this.draftData,
      assignments: this.props.assignments,
    };
    if (this.props.editing) {
      this.props.update(data);
    } else {
      this.props.add({
        ...data,
        currentFilter: this.props.currentFilter,
      });
    }
    this.handleCancel();
  };

  handleChange = (linearWizardData: Array<Object> = []) => {
    const [
      supplierDetails,
      managerReferralsSettings,
      employeeReferralsSettings,
      supplierTracking,
      manualReferralsSettings,
      assignments,
    ] = linearWizardData;

    let data = {
      ...this.draftData,
      ...supplierDetails,
      managerReferralsSettings,
      employeeReferralsSettings,
      supplierTracking,
      manualReferralsSettings,
    };

    if (supplierDetails.managerReferrals === false) delete data.managerReferralsSettings;
    if (supplierDetails.employeeReferrals === false) delete data.employeeReferralsSettings;
    if (supplierDetails.manualReferrals === false) delete data.manualReferralsSettings;
    this.props.setDraft(data);
    this.props.setAssignments((assignments && assignments.assignments) || []);
  };

  get stepData() {
    const draft = this.draftData;

    let data = [
      {
        data: {
          isActive: draft.isActive,
          name: draft.name,
          description: draft.description,
          serviceProvided: draft.serviceProvided,
          email: draft.email,
          managerReferrals: draft.managerReferrals,
          employeeReferrals: draft.employeeReferrals,
          manualReferrals: draft.manualReferrals,
        },
        valid: this.validateSupplierDetails(),
      },

      {
        data: draft.managerReferralsSettings || {},
        valid: this.validateManagerReferralsSettings(),
      },
      {
        data: draft.employeeReferralsSettings || {},
        valid: this.validateEmployeeReferralsSettings(),
      },
      { data: draft.supplierTracking || {}, valid: this.validateSupplierTracking() },
      { data: draft.manualReferralsSettings || {}, valid: this.validateManualReferralsSettings() },
      { data: {}, valid: this.validateAlertsConfiguration() },
    ];
    return data;
  }

  render() {
    const draft = this.draftData;
    const editing = this.props.editing;

    const WizardType = editing ? CollapsibleWizard : LinearWizard;
    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.handleCancel}>
          {this.props.t(editing ? "REFERRAL_EDIT_TITLE" : "REFERRAL_ADD_TITLE", {
            processId: this.props.container?.id,
          })}
        </ModalHeader>
        <ModalBody className="d-flex flex-column justify-content-start align-content-center">
          <WizardType
            defaultStep={this.props.defaultStep}
            data={this.stepData}
            onChange={this.handleChange}
            onSubmit={this.handleChange}
          >
            {/*Supplier Details*/}
            <Step
              component={StepSupplierDetails}
              title={this.props.t("SUPPLIER_DETAILS")}
              nextButtonLabel={this.nextButtonLabel.supplierDetailsLabel}
              schema={(this.schema || {}).supplierDetails}
            />

            {/*Manager Led Referrals*/}
            <Step
              component={StepManagerReferrals}
              hidden={!draft.managerReferrals}
              nextButtonLabel={this.nextButtonLabel.managerReferralsLabel}
              title={this.props.t("MANAGER_LED_REFERRALS")}
              schema={(this.schema || {}).managerLedReferrals}
            />
            {/*Employee Led Referrals*/}
            <Step
              component={StepEmployeeReferrals}
              hidden={!draft.employeeReferrals}
              draft={draft}
              title={this.props.t("EMPLOYEE_LED_REFERRALS")}
              schema={(this.schema || {}).employeeLedReferrals}
            />
            {/*Supplier Tracking*/}
            <Step
              component={StepSupplierTracking}
              nextButtonLabel={this.nextButtonLabel.supplierTrackingLabel}
              title={this.props.t("SUPPLIER_TRACKING")}
              schema={(this.schema || {}).supplierTracking}
            />
            {/*Manual Referrals*/}
            <Step
              component={StepManualReferrals}
              hidden={!draft.manualReferrals}
              nextButtonLabel={this.nextButtonLabel.manualReferralsLabel}
              title={this.props.t("MANUAL_REFERRALS")}
              schema={(this.schema || {}).manualReferralsSettings}
            />
            <Step
              assignments={this.props.assignments}
              hidden={editing}
              hierarchy={this.props.hierarchy}
              component={StepHierarchyScope}
              title={this.props.t("HIERARCHY_SCOPE")}
            />
          </WizardType>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.handleCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button materialIcon="save" disabled={!this.areStepsValid} onClick={this.handleSubmit}>
            {this.props.t("SAVE")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state): PropsFromState => {
  return {
    schema: serviceSelectors.getReferralSchema(state),
    draft: serviceSelectors.getReferralDraft(state),
    assignments: serviceSelectors.getReferralAssignments(state),
    hierarchy: sharedSelectors.getHierarchyTree(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setDraft: actionCreators.SetReferralDraft,
  setAssignments: actionCreators.SetReferralAssignments,
  add: actionCreators.AddReferral,
  update: actionCreators.UpdateReferral,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(ReferralAddEditModal);
